import type { ReactNode } from 'react';
import type { TFunction } from 'i18next';
import type { MenuProps } from 'antd';

/** Encode route / group label to sidebar catalog key (must match generate-sidebar-i18n.mjs). */
export function sidebarCatalogKey(rawKey: string | undefined, label?: ReactNode): string | null {
  if (rawKey && typeof rawKey === 'string' && rawKey.startsWith('/')) {
    return rawKey
      .replace(/^\//, '')
      .replace(/\?/g, '__')
      .replace(/=/g, '-')
      .replace(/\//g, '.')
      .replace(/[^a-zA-Z0-9._-]/g, '_');
  }
  if (typeof label === 'string' && label) {
    return `group.${label.replace(/[./]/g, '_')}`;
  }
  return null;
}

/**
 * Translate Ant Design menu items using the `sidebar` namespace.
 * Keeps icons and keys; only replaces display labels.
 */
export function translateMenuItems(
  items: MenuProps['items'] | undefined,
  t: TFunction
): MenuProps['items'] {
  if (!items) return items;
  return items.map((item) => {
    if (!item || typeof item !== 'object') return item;
    const anyItem = item as unknown as Record<string, unknown>;
    if (anyItem.type === 'divider') return item;

    const catalogKey = sidebarCatalogKey(
      typeof anyItem.key === 'string' ? anyItem.key : undefined,
      anyItem.label as ReactNode
    );
    const fallback = typeof anyItem.label === 'string' ? anyItem.label : undefined;
    let translatedLabel: ReactNode = anyItem.label as ReactNode;
    if (catalogKey && fallback) {
      // Parent route keys (e.g. fin.reports) may resolve to nested objects when children exist.
      const raw = t(catalogKey, { ns: 'sidebar', defaultValue: fallback, returnObjects: true });
      if (typeof raw === 'string') {
        translatedLabel = raw;
      } else if (raw && typeof raw === 'object') {
        const nested = raw as Record<string, unknown>;
        translatedLabel =
          typeof nested._label === 'string'
            ? nested._label
            : typeof nested.hub === 'string'
              ? nested.hub
              : fallback;
      } else {
        translatedLabel = fallback;
      }
    }

    const next: Record<string, unknown> = { ...anyItem, label: translatedLabel };
    if (Array.isArray(anyItem.children)) {
      next.children = translateMenuItems(anyItem.children as MenuProps['items'], t);
    }
    return next as unknown as NonNullable<MenuProps['items']>[number];
  });
}
